import type { CustomTemplateBox } from '@lite-workspace/dto-fotoclass-templates';
import type { DrawableData } from '../../interfaces/drawable-data.interface';
import { NormalizerFatalError } from '../errors';
import type { NormalizeIssue } from '../types';

/** One codec file per box type implements exactly `encode`/`decode` matching these signatures — see boxes/README convention. */
export type BoxEncoder<TDrawable extends DrawableData, TBox extends CustomTemplateBox> = (
  drawable: TDrawable,
  ctx: NormalizerContext,
) => TBox;

export type BoxDecoder<TBox extends CustomTemplateBox, TDrawable extends DrawableData> = (
  box: TBox,
  ctx: NormalizerContext,
) => TDrawable;

/**
 * Threaded through every codec call. `encodeDrawable`/`decodeBox` let a codec (Group) recurse into
 * its children WITHOUT importing sibling codec files — the dispatcher (normalize.ts/denormalize.ts,
 * built after all 9 codecs) supplies the real dispatch function; codecs never import each other.
 */
export interface NormalizerContext {
  readonly path: string;
  /** Logged-warn: value synthesized via a safe fallback, no information lost outright. */
  warn(reason: string): void;
  /** Logged-drop: value intentionally opposed by schema (`@deprecated ... Removed in Phase 3`). */
  drop(reason: string): void;
  /** Explicit fail — only for invariant-breaking input the codec cannot losslessly represent. */
  fail(reason: string): never;
  /** Scopes a child context to `<path>.<segment>` / `<path>[<segment>]`, sharing the same issue sink. */
  child(segment: string | number): NormalizerContext;
  encodeDrawable(drawable: DrawableData): CustomTemplateBox;
  decodeBox(box: CustomTemplateBox): DrawableData;
  /**
   * Full collage-list semantics (image+`${id}__frame` sibling expansion, positional `order`) for a
   * box array — used for the top-level collage AND for Group's own `boxes`, so a framed image nested
   * inside a group gets the same treatment as one sitting directly on the collage.
   */
  decodeBoxList(boxes: readonly CustomTemplateBox[]): readonly DrawableData[];
  /** Inverse of `decodeBoxList` — collapses an image + its `${id}__frame` neighbor back into one box. */
  encodeDrawableList(drawables: readonly DrawableData[]): readonly CustomTemplateBox[];
}

export interface CreateNormalizerContextInit {
  readonly path?: string;
  readonly onIssue?: (issue: NormalizeIssue) => void;
  readonly encodeDrawable?: (drawable: DrawableData) => CustomTemplateBox;
  readonly decodeBox?: (box: CustomTemplateBox) => DrawableData;
  readonly decodeBoxList?: (boxes: readonly CustomTemplateBox[]) => readonly DrawableData[];
  readonly encodeDrawableList?: (drawables: readonly DrawableData[]) => readonly CustomTemplateBox[];
}

function joinPath(base: string, segment: string | number): string {
  if (typeof segment === 'number') return `${base}[${segment}]`;
  return base === '' ? segment : `${base}.${segment}`;
}

function unavailableDispatch(direction: 'encodeDrawable' | 'decodeBox'): () => never {
  return (): never => {
    const who = 'a codec that recurses into children (e.g. group boxes)';
    const when = 'in tests too, not just by the real dispatcher';
    throw new Error(`NormalizerContext.${direction} was not supplied — ${who} needs it passed explicitly, ${when}`);
  };
}

/** Empty-list calls are a safe no-op even when unsupplied — only a non-empty list needs the real dispatcher. */
function unavailableListDispatch(
  direction: 'encodeDrawableList' | 'decodeBoxList',
): (items: readonly unknown[]) => readonly never[] {
  return (items: readonly unknown[]): readonly never[] => {
    if (items.length === 0) return [] as const;
    const who = 'a codec that recurses into children (e.g. group boxes)';
    const when = 'in tests too, not just by the real dispatcher';
    throw new Error(`NormalizerContext.${direction} was not supplied — ${who} needs it passed explicitly, ${when}`);
  };
}

function noopOnIssue(): void {
  // default sink when the caller doesn't care about issues (e.g. a throwaway context in a codec unit test)
}

/** Standalone factory — lets each box-codec unit test build a context without depending on the (not-yet-written) dispatcher. */
export function createNormalizerContext(init: CreateNormalizerContextInit = {}): NormalizerContext {
  const path = init.path ?? '';
  const onIssue = init.onIssue ?? noopOnIssue;
  const fallbackEncode = unavailableDispatch('encodeDrawable') as (drawable: DrawableData) => CustomTemplateBox;
  const fallbackDecode = unavailableDispatch('decodeBox') as (box: CustomTemplateBox) => DrawableData;
  const fallbackEncodeList = unavailableListDispatch('encodeDrawableList') as (
    drawables: readonly DrawableData[],
  ) => readonly CustomTemplateBox[];
  const fallbackDecodeList = unavailableListDispatch('decodeBoxList') as (
    boxes: readonly CustomTemplateBox[],
  ) => readonly DrawableData[];
  const encodeDrawable = init.encodeDrawable ?? fallbackEncode;
  const decodeBox = init.decodeBox ?? fallbackDecode;
  const encodeDrawableList = init.encodeDrawableList ?? fallbackEncodeList;
  const decodeBoxList = init.decodeBoxList ?? fallbackDecodeList;

  return {
    path,
    warn(reason: string): void {
      onIssue({ severity: 'warn', path, reason });
    },
    drop(reason: string): void {
      onIssue({ severity: 'drop', path, reason });
    },
    fail(reason: string): never {
      throw new NormalizerFatalError(reason, path);
    },
    child(segment: string | number): NormalizerContext {
      return createNormalizerContext({
        path: joinPath(path, segment),
        onIssue,
        encodeDrawable,
        decodeBox,
        encodeDrawableList,
        decodeBoxList,
      });
    },
    encodeDrawable,
    decodeBox,
    encodeDrawableList,
    decodeBoxList,
  };
}
